﻿/* https://brandplatform.smartphoto.com/general/molecules/scroll-to-top/ */
import { expressQuerySelector, isElementVisible } from "../../common/html";
import { debounce } from "../../utils/debounce";

export function createScrollToTop() {
	const scrollToTopEl = expressQuerySelector<HTMLElement>(document, ".technical-scroll-to-top", false);
	if (!scrollToTopEl) return;
	const progressPathEl = expressQuerySelector<SVGPathElement>(scrollToTopEl, ".technical-progress-path", true);

	const init = () => {
		let pathLength = 0;
		let ticking = false;
		const wobblerEl = expressQuerySelector<HTMLElement>(document, ".technical-wobbler", false);
		const stickyCtaButtonEl = expressQuerySelector<HTMLElement>(document, ".technical-mobile-cta-button", false);
		const footerEl = expressQuerySelector<HTMLElement>(document, "footer", false);

		if (wobblerEl && stickyCtaButtonEl && isElementVisible(stickyCtaButtonEl)) {
			scrollToTopEl.classList.add("m-scroll-to-top--has-wobbler--has-sticky-button");
		} else {
			if (wobblerEl) scrollToTopEl.classList.add("m-scroll-to-top--has-wobbler");
			if (stickyCtaButtonEl && isElementVisible(stickyCtaButtonEl))
				scrollToTopEl.classList.add("m-scroll-to-top--has-sticky-button");
		}

		pathLength = progressPathEl.getTotalLength(); // compute full circle length
		progressPathEl.style.strokeDasharray = `${pathLength} ${pathLength}`; // set dash pattern
		progressPathEl.style.strokeDashoffset = pathLength + "";

		const updateProgress = () => {
			const scroll = window.scrollY; // current vertical scroll offset
			const height = document.documentElement.scrollHeight - window.innerHeight; // total scrollable area
			const progress = pathLength - (scroll * pathLength) / height; // proportional progress value

			progressPathEl.style.strokeDashoffset = progress + ""; // update the circle
		};

		function handleScroll() {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					updateProgress();
					scrollToTopEl.classList.toggle("m-scroll-to-top--active-progress", window.scrollY > 50);
					ticking = false;
				});
				ticking = true;
			}
		}

		const isIOS = (): boolean => {
			const iosQuirkPresent = () => {
				const audio = new Audio();
				audio.volume = 0.5;
				return audio.volume === 1; // volume cannot be changed from "1" on iOS 12 and below
			};
			const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
			const isAppleDevice = navigator.userAgent.indexOf("Macintosh") !== 0;
			const isTouchScreen = navigator.maxTouchPoints >= 1; // true for iOS 13
			return isIOS || (isAppleDevice && (isTouchScreen || iosQuirkPresent()));
		};

		const setBottomStyle = () => {
			if (stickyCtaButtonEl && isElementVisible(stickyCtaButtonEl)) {
				if (footerEl) footerEl.style.marginBottom = 10 + stickyCtaButtonEl.clientHeight + "px";
				scrollToTopEl.style.bottom =
					10 +
					stickyCtaButtonEl.clientHeight +
					parseFloat(
						window.getComputedStyle(stickyCtaButtonEl.parentElement, null).getPropertyValue("padding-bottom"),
					) +
					"px";
			} else {
				scrollToTopEl.style.bottom = "";
				if (footerEl) footerEl.style.marginBottom = "";
			}
		};

		const onResize = () => {
			if (!stickyCtaButtonEl) return;
			if (isElementVisible(stickyCtaButtonEl)) {
				if (wobblerEl) scrollToTopEl.classList.add("m-scroll-to-top--has-wobbler--has-sticky-button");
				else scrollToTopEl.classList.add("m-scroll-to-top--has-sticky-button");
			} else {
				if (wobblerEl) {
					scrollToTopEl.classList.remove("m-scroll-to-top--has-wobbler--has-sticky-button");
					scrollToTopEl.classList.add("m-scroll-to-top--has-wobbler");
				} else {
					scrollToTopEl.classList.remove("m-scroll-to-top--has-sticky-button");
				}
			}
			setBottomStyle();
		};

		window.addEventListener("scroll", () => {
			handleScroll();
		});
		const debouncedResize = debounce(onResize, 100);
		window.addEventListener("resize", debouncedResize);

		scrollToTopEl.addEventListener("click", () => {
			window.scrollTo({ top: 0, behavior: "smooth" });
			if (isIOS())
				debounce(() => {
					window.scrollTo({ top: 0, behavior: "smooth" });
				}, 100);
		});

		updateProgress();
		setBottomStyle();
	};

	init();
}
